import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { JOB_LABEL, cn } from '../../../components/TwoDays/model/derive';
import { MIN, dur } from '../../../components/TwoDays/model/dashboard';
import useMedia, { MOBILE } from '../../../components/TwoDays/useMedia';
import useLiveBoard from '../../../components/TwoDays/ops/useLiveBoard';
import { dt, hm, kstAt } from '../../../components/TwoDays/ops/liveBoardModel';
import { Avatar, CardHead, LogList, Pill, PillButton, SectionHead, Tag, card, cardPad, listBox } from '../../../components/TwoDays/ops/opsUi';
import {
  CallDialog,
  CancelDialog,
  DropDialog,
  DueDialog,
  NoteDialog,
  PeerCallDialog,
  PublishDialog,
  ReassignDialog,
  ReceiverDialog,
} from '../../../components/TwoDays/ops/OpsDialogs';

/**
 * Ops › Live board (시안 live-board-content) — All teams 와 팀 하나를 오간다 (/live · /live/:teamId).
 *   머리: ● Active · 범위 · 지금 KST · 남은 시간 / 제목 / Send announcement · 팀 고르기 알약 + 정렬 안내
 *   All teams  팀 요약 카드 → Needs ops(팀 이름 앞) → Ops log
 *   Team       Needs ops → Team progress → Members → Tasks → Ops log
 * Needs ops 줄을 누르면 태스크 상세(O3), 줄 끝 CTA 는 바로 대화상자.
 */

const DOT = { failed: 'bg-functional-failed03', warning: 'bg-functional-warning03', info: 'bg-grayscale-gray05', neutral: 'bg-grayscale-gray05' };
const META = { failed: 'text-functional-failed03', warning: 'text-functional-warning04', secondary: 'text-text-secondary' };
const SIG_DOT = { failed: 'bg-functional-failed03', warning: 'bg-functional-warning03', neutral: 'bg-grayscale-gray05' };
const SIG_TEXT = { failed: 'text-functional-failed03', warning: 'text-functional-warning04', neutral: 'text-text-secondary' };

/** "Needs ops" 빨간 수 배지 18px */
const HotBadge = ({ n }) =>
  n > 0 ? (
    <span title='Needs ops' className='inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-functional-failed03 px-[5px] text-[11px] font-medium tabular-nums text-grayscale-gray00'>{n}</span>
  ) : null;

/** 신호 — 점 + 글자 */
export const Signals = ({ sigs, className }) =>
  sigs.length ? (
    <span className={cn('flex min-w-0 flex-wrap gap-x-2.5 gap-y-1 text-[12px] leading-[140%]', className)}>
      {sigs.map((s) => (
        <span key={s.label} className={cn('inline-flex items-center gap-1.5 whitespace-nowrap', SIG_TEXT[s.tone])}>
          <span className={cn('h-1.5 w-1.5 rounded-full', SIG_DOT[s.tone])} />
          {s.label}
        </span>
      ))}
    </span>
  ) : null;

/* ── 팀 고르기 ──────────────────────────────────────── */

const TeamPicker = ({ teams, current, onPick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const mobile = useMedia(MOBILE);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const key = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', key);
    };
  }, [open]);
  const total = teams.reduce((n, t) => n + t.summary.needs, 0);
  const opts = [
    { id: null, label: 'All teams', sub: `${teams.length} teams · project view`, n: total },
    ...teams.map((t) => ({ id: t.team.id, label: `Team ${t.team.name}`, sub: `Starts ${dt(t.data.project.startAt)} · created ${dt(t.team.createdAt)} · ${t.summary.approved} approved · ends ${kstAt(t.data.project.endAt)}`, n: t.summary.needs })),
  ];
  const selId = current?.team.id ?? null;
  return (
    <div className='relative -mt-2 flex flex-wrap items-center gap-x-3 gap-y-2'>
      <div ref={ref} className='relative max-w-full'>
        <button type='button' aria-haspopup='listbox' aria-expanded={open} onClick={() => setOpen((v) => !v)} className={cn('inline-flex max-w-full items-center gap-2 rounded-full border border-green-deep03/20 bg-grayscale-gray00 pl-4 pr-3 text-[14px] font-medium text-text-primary hover:bg-green-deep03/[0.03]', mobile ? 'h-11' : 'h-10')}>
          <span className='text-[12px] font-normal text-text-secondary'>Team</span>
          <span className='min-w-0 truncate'>{current ? current.team.name : 'All teams'}</span>
          <HotBadge n={current ? current.summary.needs : total} />
          <ChevronIcon className={cn('h-[18px] w-[18px] shrink-0 text-icon-secondary transition-transform', open && 'rotate-180')} />
        </button>
        {open && (
          <div role='listbox' aria-label='Teams' className={cn('absolute left-0 top-[calc(100%+8px)] z-30 box-border flex max-w-full flex-col gap-0.5 rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-1.5 shadow-popup-01', mobile ? 'w-[calc(100vw-32px)]' : 'w-[400px]')}>
            {opts.map((o) => {
              const on = o.id === selId;
              return (
                <button key={o.id ?? 'all'} type='button' role='option' aria-selected={on} onClick={() => { onPick(o.id); setOpen(false); }} className={cn('grid grid-cols-[16px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left hover:bg-green-deep03/[0.06]', mobile ? 'min-h-[52px]' : 'min-h-[44px]', on && 'bg-surface-green10')}>
                  <span className='flex h-4 w-4 items-center justify-center'>
                    <span className={cn('h-2 w-2 rounded-full', on ? 'bg-green-deep02' : 'bg-transparent')} />
                  </span>
                  <span className='flex min-w-0 flex-col gap-0.5'>
                    <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{o.label}</span>
                    <span className='text-[12px] leading-[140%] text-text-secondary [text-wrap:pretty]'>{o.sub}</span>
                  </span>
                  <HotBadge n={o.n} />
                </button>
              );
            })}
          </div>
        )}
      </div>
      <span className='text-[12px] leading-[140%] text-text-tertiary'>Sorted by start · created · progress · end</span>
    </div>
  );
};

/* ── Needs ops ──────────────────────────────────────── */

const NeedsOps = ({ rows, onRow, onCta }) => {
  const mobile = useMedia(MOBILE);
  return (
    <section className={cn(card, cardPad, 'flex min-w-0 flex-col gap-4')}>
      <CardHead title='Needs ops' sub='Accept waits, open loops, dropped members, peer review and approval flags · oldest first' right={`${rows.length} open`} />
      {rows.length === 0 ? (
        <div className='flex flex-col items-center gap-2 rounded-xl border border-dashed border-green-deep03/[0.24] px-4 py-7 text-center'>
          <span className='text-[16px] font-medium leading-[140%] text-text-primary'>Nothing needs ops right now</span>
          <span className='text-[14px] leading-[150%] text-text-secondary'>Accept waits over 30 minutes, open questions and approval flags show up here.</span>
        </div>
      ) : (
        <div role='list' className={cn(listBox, 'overflow-hidden')}>
          {rows.map((r, i) => (
            <div key={`${r.teamId}:${r.key}`} role='listitem' onClick={() => onRow(r)} className={cn('grid cursor-pointer grid-cols-[8px_minmax(0,1fr)_auto] items-center gap-3 py-3 pl-4 pr-3.5 hover:bg-green-deep03/[0.04]', i > 0 && 'border-t border-green-deep03/[0.12]')}>
              <span className={cn('h-2 w-2 rounded-full', DOT[r.tone])} />
              <div className='flex min-w-0 flex-col gap-0.5'>
                <span className='flex flex-wrap items-center gap-2 text-[14px] font-medium leading-[140%] text-text-primary'>
                  <span className='min-w-0'>
                    {r.teamName && <span className='text-text-secondary'>{r.teamName} · </span>}
                    {r.title}
                  </span>
                  {r.kind && <Tag>{r.kind}</Tag>}
                  {r.ref && <Tag title='Shown to ops only · doesn’t change the approval'>For reference</Tag>}
                </span>
                <span className={cn('text-[13px] leading-[140%] [text-wrap:pretty]', META[r.metaTone] ?? META.secondary)}>{r.meta}</span>
              </div>
              {r.cta && r.cta.action !== 'none' ? (
                <PillButton size={mobile ? 'l' : 's'} variant={r.primary ? 'primary' : 'soft'} className={cn(!mobile && 'h-8')} onClick={(e) => { e.stopPropagation(); onCta(r); }}>
                  {r.cta.label}
                </PillButton>
              ) : (
                <span className='text-[13px] text-text-tertiary'>No action</span>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/* ── Team progress ──────────────────────────────────── */

const TeamProgress = ({ team }) => {
  const { chain, prog, workload } = team;
  const mobile = useMedia(MOBILE);
  const desk = useMedia('(min-width: 1180px)');
  return (
    <section aria-label='Team progress' className={cn(card, cardPad, 'flex min-w-0 flex-col gap-5')}>
      <CardHead title='Team progress' sub='Handoff chain, progress and workload · same data as the Dashboard' />
      <div className='flex flex-col gap-2'>
        <span className='text-[13px] font-medium leading-[140%] text-text-primary'>Handoff chain</span>
        <ol className={cn('grid gap-2', mobile ? 'grid-cols-1' : 'grid-cols-4')}>
          {chain.map((c) => (
            <li key={c.k} className={cn('flex min-w-0 flex-col gap-0.5 rounded-xl px-3 py-2.5', c.active ? 'bg-green-deep03/[0.08]' : 'bg-grayscale-gray00', c.late ? 'shadow-[inset_0_0_0_1px_rgba(255,69,80,0.4)]' : c.active ? 'shadow-[inset_0_0_0_1px_rgba(0,158,79,0.4)]' : 'shadow-[inset_0_0_0_1px_rgba(0,158,79,0.12)]')}>
              <span className='flex items-baseline justify-between gap-2'>
                <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{c.name}</span>
                <span className={cn('text-right text-[12px] font-medium leading-[140%]', c.late ? 'text-functional-failed03' : c.done ? 'text-functional-success04' : c.active ? 'text-green-deep04' : 'text-text-tertiary')}>{c.status}</span>
              </span>
              <span className='truncate text-[12px] leading-[140%] text-text-secondary'>{c.who || '–'}</span>
              <span className='text-[12px] leading-[140%] tabular-nums text-text-secondary'>Due {dt(c.due)}</span>
              {c.adjusted && <span className='text-[12px] leading-[140%] tabular-nums text-functional-warning04'>Adjusted {dt(c.adjusted)} · +{dur(c.adjMin)}</span>}
            </li>
          ))}
          {chain.length === 0 && <li className='text-[13px] text-text-secondary'>No stages yet</li>}
        </ol>
      </div>
      <div className={cn('grid gap-x-8 gap-y-5', desk ? 'grid-cols-2' : 'grid-cols-1')}>
        <div className='flex min-w-0 flex-col gap-2.5'>
          <span className='text-[13px] font-medium leading-[140%] text-text-primary'>Progress · {prog.big}</span>
          <div className='flex h-2.5 overflow-hidden rounded-full bg-green-deep03/[0.08]'>
            {prog.segs.filter((s) => s.n).map((s) => (
              <span key={s.l} title={`${s.l} · ${s.n}`} className={cn('h-full', s.c)} style={{ width: s.w }} />
            ))}
          </div>
          <div className='grid grid-cols-2 gap-x-4 gap-y-1.5'>
            {prog.segs.map((s) => (
              <span key={s.l} className='flex items-center gap-2 text-[12px] leading-[140%] text-text-secondary'>
                <span className={cn('h-2 w-2 shrink-0 rounded-[2px]', s.c)} />
                <span className='min-w-0 flex-1'>{s.l}</span>
                <span className='font-medium tabular-nums text-text-primary'>{s.n}</span>
              </span>
            ))}
          </div>
          <span className='text-[12px] leading-[140%] text-text-secondary'>{prog.blk}</span>
        </div>
        <div className='flex min-w-0 flex-col gap-2.5'>
          <span className='text-[13px] font-medium leading-[140%] text-text-primary'>Workload</span>
          <div className={listBox}>
            {workload.map((w, i) => (
              <div key={w.id} className={cn('grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 py-2', i > 0 && 'border-t border-green-deep03/[0.1]')}>
                <span className='flex min-w-0 flex-col'>
                  <span className='text-[13px] font-medium leading-[140%] text-text-primary'>
                    {w.name} <span className='font-normal text-text-secondary'>{JOB_LABEL[w.job] ?? w.job}</span>
                  </span>
                  <span className={cn('text-[12px] leading-[140%] tabular-nums', w.offline ? 'text-text-tertiary' : 'text-text-secondary')}>{w.left}</span>
                </span>
                <span className={cn('whitespace-nowrap text-[13px] font-medium leading-[140%]', w.n >= 3 ? 'text-functional-failed03' : 'text-text-primary')}>{w.n} in progress</span>
              </div>
            ))}
            {workload.length === 0 && <span className='px-3 py-2 text-[13px] text-text-secondary'>No active members</span>}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Members ────────────────────────────────────────── */

const MemberMenu = ({ row, onNote, onDrop, mobile }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div ref={ref} className='relative'>
      <button type='button' aria-label={`Actions for ${row.m.name}`} aria-haspopup='menu' aria-expanded={open} onClick={() => setOpen((v) => !v)} className={cn('flex items-center justify-center gap-[3px] rounded-full hover:bg-green-deep03/[0.08]', mobile ? 'h-11 w-11' : 'h-8 w-8', open && 'bg-green-deep03/[0.08]')}>
        {[0, 1, 2].map((i) => (
          <span key={i} className='h-1 w-1 rounded-full bg-icon-secondary' />
        ))}
      </button>
      {open && (
        <div role='menu' className='absolute right-0 top-[calc(100%+6px)] z-20 flex w-52 flex-col rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-1.5 shadow-popup-01'>
          <button type='button' role='menuitem' onClick={() => { setOpen(false); onNote(); }} className='flex h-10 items-center rounded-[10px] px-3 text-left text-[14px] text-text-primary hover:bg-green-deep03/[0.06]'>
            Add note
          </button>
          {row.presence.kind !== 'dropped' && (
            <button type='button' role='menuitem' onClick={() => { setOpen(false); onDrop(); }} className='flex h-10 items-center rounded-[10px] px-3 text-left text-[14px] text-functional-failed03 hover:bg-green-deep03/[0.06]'>
              Mark as dropped
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const PRES_DOT = { active: 'bg-green-deep02', offline: 'bg-grayscale-gray05', dropped: 'bg-grayscale-gray05' };
const PRES_TEXT = { active: 'text-text-primary', offline: 'text-text-secondary', dropped: 'text-text-tertiary' };

const Members = ({ team, onNote, onDrop }) => {
  const mobile = useMedia(MOBILE);
  return (
    <section className={cn(card, cardPad, 'flex min-w-0 flex-col gap-4')}>
      <CardHead title='Members' sub='Status is set by work hours · only ops mark a member as dropped' />
      <div role='list' className={listBox}>
        {team.members.map((r, i) => (
          <div key={r.m.id} role='listitem' className={cn('relative grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 py-2.5 pl-3.5 pr-2', i > 0 && 'border-t border-green-deep03/[0.12]', r.presence.kind === 'dropped' && 'opacity-[0.72]')}>
            <Avatar name={r.m.name} />
            <div className='flex min-w-0 flex-col gap-0.5'>
              <span className='flex flex-wrap items-center gap-1.5 text-[14px] font-medium leading-[140%] text-text-primary'>
                {r.m.name}
                <span className='text-[12px] font-normal text-text-secondary'>{JOB_LABEL[r.m.job] ?? r.m.job}</span>
                {r.tag && <Tag>{r.tag}</Tag>}
              </span>
              <span className='flex min-w-0 items-center gap-1.5 text-[12px] leading-[140%] text-text-secondary'>
                <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', PRES_DOT[r.presence.kind])} />
                <span className={cn('shrink-0', PRES_TEXT[r.presence.kind])}>{r.statusLabel}</span>
                <span className='min-w-0 truncate'>· {r.now}</span>
              </span>
            </div>
            <MemberMenu row={r} mobile={mobile} onNote={() => onNote(r)} onDrop={() => onDrop(r)} />
          </div>
        ))}
      </div>
    </section>
  );
};

/* ── Tasks ──────────────────────────────────────────── */

const Tasks = ({ team, to }) => {
  const narrow = useMedia('(max-width: 759px)');
  const desk = useMedia('(min-width: 1180px)');
  const rows = team.tasks;
  const openN = rows.filter((r) => !['approved', 'cancelled'].includes(r.t.state)).length;
  const cols = desk ? 'grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1fr)_140px_112px_minmax(0,1.4fr)]' : 'grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_132px_96px_minmax(0,1.2fr)]';
  return (
    <section className={cn(card, cardPad, 'flex min-w-0 flex-col gap-4')}>
      <CardHead title='Tasks' sub='Open a task to call, change due, reassign, change receiver or cancel' right={`${openN} open · ${rows.length} total`} />
      {narrow ? (
        <div role='list' className={cn(listBox, 'overflow-hidden')}>
          {rows.map((r, i) => (
            <Link key={r.t.id} role='listitem' to={to(r.t.id)} className={cn('flex flex-col gap-1.5 px-4 py-3 hover:bg-green-deep03/[0.03]', i > 0 && 'border-t border-green-deep03/[0.12]')}>
              <span className='flex items-start justify-between gap-3'>
                <span className={cn('min-w-0 text-[14px] font-medium leading-[140%]', r.t.state === 'cancelled' ? 'text-text-tertiary' : 'text-text-primary')}>{r.t.title}</span>
                <Pill tone={r.chip.tone}>{r.chip.label}</Pill>
              </span>
              <span className='text-[13px] leading-[140%] tabular-nums text-text-secondary'>
                {r.assigneeName} → {r.receiverName} · due {r.dueT}
                {r.adjusted ? ` · adjusted ${hm(r.adjusted)}` : ''}
              </span>
              <Signals sigs={r.sigs} />
            </Link>
          ))}
        </div>
      ) : (
        <div className={cn(listBox, 'overflow-hidden')}>
          <div className={cn('grid h-10 items-center gap-4 bg-green-deep03/[0.03] px-4 text-[12px] font-medium text-text-secondary', cols)}>
            <span>Task</span>
            <span>Assignee</span>
            <span>Receiver</span>
            <span>State</span>
            <span>Due</span>
            <span>Signals</span>
          </div>
          {rows.map((r) => (
            <Link key={r.t.id} to={to(r.t.id)} className={cn('grid min-h-[52px] items-center gap-4 border-t border-green-deep03/[0.08] px-4 py-2 text-[14px] leading-[140%] hover:bg-green-deep03/[0.03]', cols)}>
              <span className={cn('min-w-0 truncate font-medium', r.t.state === 'cancelled' ? 'text-text-tertiary' : 'text-text-primary')}>{r.t.title}</span>
              <span className='min-w-0 truncate text-text-primary'>{r.assigneeName}</span>
              <span className='min-w-0 truncate text-text-secondary'>{r.receiverName}</span>
              <span>
                <Pill tone={r.chip.tone}>{r.chip.label}</Pill>
              </span>
              <span className='flex flex-col tabular-nums'>
                <span>{r.dueT}</span>
                {r.adjT && <span className='text-[12px] text-functional-warning04'>{r.adjT}</span>}
                {r.t.extendedReason && <span className='text-[12px] text-text-secondary'>Changed by ops</span>}
              </span>
              <Signals sigs={r.sigs} />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

/* ── Ops log ────────────────────────────────────────── */

const KEY_KINDS = ['call', 'peer_call', 'publish', 'drop', 'restore', 'reassign', 'receiver', 'cancel', 'due'];
/** 시안 teamLog 문구 — 공지는 "Published an announcement" + "제목 — 본문" */
const logTitle = (l) => {
  if (l.kind === 'publish') return 'Published an announcement';
  return l.text;
};
const logDetail = (l) => {
  if (l.kind === 'publish') return `${l.text.replace(/^(Announcement|Change event) · /, '')}${l.detail ? ` — ${l.detail}` : ''}`;
  return l.detail ?? '';
};
export const logEvents = (logs, teamName) =>
  logs.map((l) => ({
    n: `ops-${l.id}`,
    key: KEY_KINDS.includes(l.kind),
    ops: true,
    opsOnly: l.opsOnly,
    who: 'Ops',
    title: teamName && l.teamId != null ? `${teamName(l.teamId)} · ${logTitle(l)}` : logTitle(l),
    detail: logDetail(l),
    time: dt(l.createdAt),
    at: l.createdAt,
  }));

const OpsLog = ({ logs, teamName }) => (
  <section className='flex min-w-0 flex-col gap-3'>
    <SectionHead title='Ops log' sub='Member and change-event actions · newest first' />
    <LogList events={logEvents(logs, teamName)} empty='No ops actions yet' />
  </section>
);

/* ── All teams 요약 카드 ─────────────────────────────── */

const TeamCards = ({ teams, to }) => {
  const mobile = useMedia(MOBILE);
  return (
    <div className={cn('grid gap-4 sm:gap-6', mobile ? 'grid-cols-1' : 'grid-cols-2')}>
      {teams.map((t) => (
        <Link key={t.team.id} to={to(t.team.id)} aria-label={`Open ${t.team.name}`} className={cn(card, cardPad, 'flex min-w-0 flex-col gap-4 transition-colors hover:border-green-deep03/30')}>
          <span className='flex items-start justify-between gap-3'>
            <span className='flex min-w-0 flex-col gap-0.5'>
              <span className='text-[15px] font-medium leading-[140%] text-text-primary'>Team {t.team.name}</span>
              <span className='text-[13px] leading-[140%] text-text-secondary'>{t.summary.leader} · Leader</span>
            </span>
            <span className='flex shrink-0 items-center gap-1 text-[13px] font-medium leading-[140%] text-green-deep04'>
              Open team
              <ChevronIcon className='h-4 w-4 -rotate-90' />
            </span>
          </span>
          <span className='flex flex-col gap-2'>
            <span className='flex items-baseline justify-between gap-3 text-[13px] leading-[140%]'>
              <span className='font-medium text-text-primary'>{t.summary.stage}</span>
              <span className='tabular-nums text-text-secondary'>{t.summary.approved} of {t.summary.denom} approved</span>
            </span>
            <span className='h-2 overflow-hidden rounded-full bg-green-deep03/[0.08]'>
              <span className='block h-full rounded-full bg-green-deep02' style={{ width: `${t.summary.pct}%` }} />
            </span>
          </span>
          <span className='flex flex-col gap-1.5 text-[13px] leading-[140%]'>
            <span className='flex items-center gap-2'>
              <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', t.summary.needs ? 'bg-functional-warning03' : 'bg-green-deep02')} />
              <span className={cn('font-medium', t.summary.needs ? 'text-functional-warning04' : 'text-text-secondary')}>{t.summary.needsLabel}</span>
              {t.summary.blocked > 0 && <span className='text-functional-failed03'>· {t.summary.blocked} blocked</span>}
            </span>
            <span className='text-text-secondary'>{t.summary.people}</span>
          </span>
        </Link>
      ))}
    </div>
  );
};

/* ── 화면 ───────────────────────────────────────────── */

const OpsLiveBoard = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const mobile = useMedia(MOBILE);
  const { project, teams, logs, now, error, act, loading } = useLiveBoard();
  const [dialog, setDialog] = useState(null); // { kind, team, row, member, label }
  const current = useMemo(() => teams?.find((t) => String(t.team.id) === String(teamId)) ?? null, [teams, teamId]);
  const teamName = (id) => teams?.find((t) => String(t.team.id) === String(id))?.team.name ?? '';
  const taskTo = (id) => `/2days/ops/live/tasks/${id}`;
  const close = () => setDialog(null);

  if (!project) return <p className='py-10 text-center text-[14px] text-text-secondary'>Pick a project above.</p>;
  if (project.status === 'draft') {
    return (
      <div className='flex flex-col gap-4'>
        <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>Live board</h1>
        <p className={cn(card, 'px-6 py-12 text-center text-[14px] text-text-secondary')}>The live board opens once the project starts.</p>
      </div>
    );
  }
  if (loading || !teams) return <p className='py-10 text-center text-[14px] text-text-secondary'>{error || 'Loading…'}</p>;

  const end = teams[0] ? new Date(teams[0].data.project.endAt) : null;
  const ended = !end || now >= end;
  const needsRows = current
    ? current.needs.map((n) => ({ ...n, teamId: current.team.id, team: current }))
    : teams.flatMap((t) => t.needs.map((n) => ({ ...n, teamId: t.team.id, teamName: t.team.name, team: t }))).sort((a, b) => a.at - b.at);
  const logRows = current ? logs.filter((l) => l.teamId === current.team.id || l.teamId == null) : logs;

  const onCta = (r) => {
    const team = r.team;
    const row = r.taskId ? team.taskRow(r.taskId) : null;
    if (r.cta.action === 'open') return navigate(taskTo(r.taskId));
    if (r.cta.action === 'inbox') return navigate(`/2days/ops/leads/${r.taskId}`);
    if (r.cta.action === 'peer_call') return setDialog({ kind: 'peer_call', team, member: team.memberById.get(r.memberId), label: r.meta });
    if (r.cta.action === 'none') return null;
    return setDialog({ kind: r.cta.action, team, row });
  };
  const onRow = (r) => {
    if (r.taskId) navigate(r.cta?.action === 'inbox' ? `/2days/ops/leads/${r.taskId}` : taskTo(r.taskId));
    else onCta(r);
  };

  const dialogs = dialog && (
    <>
      {dialog.kind === 'call' && <CallDialog row={dialog.row} onAct={act} onClose={close} />}
      {dialog.kind === 'peer_call' && <PeerCallDialog member={dialog.member} label={dialog.label} onAct={act} onClose={close} />}
      {dialog.kind === 'reassign' && <ReassignDialog row={dialog.row} team={dialog.team} onAct={act} onClose={close} />}
      {dialog.kind === 'receiver' && <ReceiverDialog row={dialog.row} team={dialog.team} onAct={act} onClose={close} />}
      {dialog.kind === 'due' && <DueDialog row={dialog.row} endAt={dialog.team.data.project.endAt} onAct={act} onClose={close} />}
      {dialog.kind === 'cancel' && <CancelDialog row={dialog.row} onAct={act} onClose={close} />}
      {dialog.kind === 'drop' && <DropDialog member={dialog.member} openTasks={dialog.team.tasks.filter((x) => x.t.assigneeId === dialog.member.id && !['approved', 'cancelled'].includes(x.t.state)).length} onAct={act} onClose={close} />}
      {dialog.kind === 'note' && <NoteDialog teamId={dialog.team.team.id} member={dialog.member} onAct={act} onClose={close} />}
      {dialog.kind === 'publish' && <PublishDialog teamId={current?.team.id ?? null} teamName={current?.team.name} onAct={act} onClose={close} />}
    </>
  );

  return (
    <div className={cn('flex flex-col', mobile ? 'gap-5' : 'gap-7')}>
      <div className='flex flex-wrap items-end justify-between gap-x-4 gap-y-3'>
        <div className='flex min-w-0 flex-col gap-1.5'>
          <span className='flex flex-wrap items-center gap-2 text-[13px] leading-[140%] text-text-secondary'>
            <span className={cn('h-2 w-2 rounded-full', ended ? 'bg-grayscale-gray05' : 'bg-green-deep02')} />
            <span className='font-medium text-text-primary'>{ended ? 'Ended' : 'Active'}</span>· {current ? `Team ${current.team.name}` : `${teams.length} teams`} · {kstAt(now)}
            {!ended && ` · ${dur((end - now) / MIN)} left`}
          </span>
          <h1 className={cn('m-0 font-medium leading-[120%] tracking-[-0.01em] text-text-primary', mobile ? 'text-[24px]' : 'text-[28px]')}>{current ? `Team ${current.team.name}` : 'Live board'}</h1>
        </div>
        <PillButton size={mobile ? 'l' : 'm'} onClick={() => setDialog({ kind: 'publish' })} disabled={ended} tip='The project has ended'>
          Send announcement
        </PillButton>
      </div>
      <TeamPicker teams={teams} current={current} onPick={(id) => navigate(id == null ? '/2days/ops/live' : `/2days/ops/live/${id}`)} />

      {error && <p className='text-[14px] text-functional-failed04'>{error}</p>}

      {!current ? (
        <>
          <TeamCards teams={teams} to={(id) => `/2days/ops/live/${id}`} />
          <NeedsOps rows={needsRows} onRow={onRow} onCta={onCta} />
          <OpsLog logs={logRows} teamName={teamName} />
        </>
      ) : (
        <>
          <NeedsOps rows={needsRows} onRow={onRow} onCta={onCta} />
          <TeamProgress team={current} />
          <Members team={current} onNote={(r) => setDialog({ kind: 'note', team: current, member: r.m })} onDrop={(r) => setDialog({ kind: 'drop', team: current, member: r.m })} />
          <Tasks team={current} to={taskTo} />
          <OpsLog logs={logRows} />
        </>
      )}
      {dialogs}
    </div>
  );
};

export default OpsLiveBoard;
